import i18n from "../i18n";
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X, WifiOff } from "lucide-react";
import BackOnline from "@/components/BackOnline";

const DISMISS_KEY = "ajfarm:pwa-install-dismissed-at";
const DISMISS_HIDE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export default function PWAInstallPrompt() {
    const [deferred, setDeferred] = useState(null);
    const [visible, setVisible] = useState(false);
    const [offline, setOffline] = useState(!navigator.onLine);

    useEffect(() => {
        // Hide if recently dismissed or already installed
        const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
        const recentlyDismissed = dismissedAt && Date.now() - dismissedAt < DISMISS_HIDE_MS;
        const standalone = window.matchMedia("(display-mode: standalone)").matches ||
            window.navigator.standalone === true;

        const onBip = (e) => {
            e.preventDefault();
            setDeferred(e);
            if (!recentlyDismissed && !standalone) setVisible(true);
        };
        const onInstalled = () => {
            setVisible(false);
            setDeferred(null);
        };
        const onOnline = () => {
            setOffline(false);
            // show a brief back-online indicator
            const ev = new CustomEvent('ajfarm:back-online');
            window.dispatchEvent(ev);
        };
        const onOffline = () => setOffline(true);

        window.addEventListener("beforeinstallprompt", onBip);
        window.addEventListener("appinstalled", onInstalled);
        window.addEventListener("online", onOnline);
        window.addEventListener("offline", onOffline);
        return () => {
            window.removeEventListener("beforeinstallprompt", onBip);
            window.removeEventListener("appinstalled", onInstalled);
            window.removeEventListener("online", onOnline);
            window.removeEventListener("offline", onOffline);
        };
    }, []);

    const install = async () => {
        if (!deferred) return;
        try {
            deferred.prompt();
            await deferred.userChoice;
        } catch (e) {
            // ignore
        }
        setDeferred(null);
        setVisible(false);
    };

    const dismiss = () => {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
        setVisible(false);
    };

    return (
        <>
            {offline && (
                <div
                    className="fixed top-2 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 rounded-full bg-amber-500 text-white px-4 py-1.5 shadow-lg text-sm font-semibold"
                    data-testid="offline-indicator"
                    role="status"
                >
                    <WifiOff className="w-4 h-4" />
                    {i18n.t("final.81928ede6ce6")}
                </div>
            )}
            {/* Back online transient message */}
            <BackOnline />
            {visible && deferred && (
                <div
                    className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[calc(100vw-2rem)] max-w-md rounded-2xl bg-primary text-white shadow-2xl p-4 flex items-center gap-3"
                    data-testid="pwa-install-banner"
                >
                    <div className="w-10 h-10 rounded-xl bg-white/20 grid place-items-center flex-shrink-0">
                        <Download className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm">{i18n.t("final.936c86894f66")}</div>
                        <div className="text-xs opacity-90">{i18n.t("final.3b158d60607f")}</div>
                    </div>
                    <Button
                        onClick={install}
                        className="bg-white text-primary hover:bg-white/90 h-9 px-3 font-bold"
                        data-testid="pwa-install-btn"
                    >
                        {i18n.t("final.fd6c3ebf7bef")}
                    </Button>
                    <button
                        onClick={dismiss}
                        aria-label={i18n.t("final.b766753f6fa4")}
                        className="text-white/80 hover:text-white"
                        data-testid="pwa-install-dismiss"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            )}
        </>
    );
}
